import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import ArrowIcon from '../ui/ArrowIcon';
import Corners from '../ui/Corners';
import SectionHeader from '../ui/SectionHeader';
import './Projects.css';

const EASE = [0.22, 1, 0.36, 1];

const PROJECTS = [
  {
    id: 'footwork',
    featured: true,
    wide: true,
    badge: 'Live app',
    title: 'Footwork Coach',
    subtitle: 'Browser-based kickboxing footwork trainer with on-device pose estimation',
    description: 'A kickboxing footwork trainer that runs entirely in the browser. The camera feed goes through MediaPipe PoseLandmarker — 33 body landmarks per frame, WASM with GPU/CPU fallback — and every drill is broken down by direction, distance, timing, and rhythm. It detects steps, diagonals, dashes, pivots, stance switches, shuffle rhythm, and jab/cross, and an animated demo fighter performs each drill before you try it. Timed rounds come with spoken cues and a bell, custom combinations can be built from any detectable move, and every session gets a score, a footwork trajectory diagram, and a comparison against your previous one. No backend, no uploads: frames are processed on-device and discarded.',
    tags: ['React 19', 'TypeScript', 'Vite', 'MediaPipe', 'Pose Estimation', 'Computer Vision', 'Web Audio', 'Web Speech API', 'Cloudflare Pages'],
    icon: '🥊',
    color: '#FF5C3A',
    highlights: [
      { label: 'Landmarks', value: '33 / frame' },
      { label: 'Inference', value: 'On-device' },
      { label: 'Backend', value: 'None' },
    ],
    github: 'https://github.com/nevaerdogan/FootworkCoach',
    website: 'https://footworkcoach.pages.dev',
  },
  {
    id: 'gloath',
    featured: true,
    badge: 'PM case study',
    title: 'Gloath',
    subtitle: 'Mobile match-2 game, designed end-to-end as a product management case study',
    description: 'Designed a mobile match-2 game from scratch as an end-to-end product management case study. Engineered a 4-loop retention system (match → merge → dig → collect), a dual-currency economy with conversion moment mapping, and a RICE-prioritised feature roadmap. Includes a full KPI framework grounded in casual gaming benchmarks, risk/assumption log, monetisation funnel engineering, and a phased launch roadmap.',
    tags: ['Product Management', 'RICE Framework', 'Retention Design', 'Game Design', 'Monetisation', 'KPI Framework', 'Dual-Currency Economy'],
    icon: '🎮',
    color: '#A855F7',
    highlights: [
      { label: 'Retention', value: '4-loop system' },
      { label: 'Economy', value: 'Dual-currency' },
      { label: 'Prioritisation', value: 'RICE' },
    ],
    caseStudy: '/gloath',
  },
  {
    id: 'eps',
    featured: true,
    badge: 'Hackathon',
    title: 'EPS Winner Agent',
    subtitle: 'Autonomous earnings forecasting agent — Agents vs Wall Street, London',
    description: 'A multi-agent system that forecasts company financial metrics from historical SEC filings and live web retrieval, with zero human analyst input. Built at "Agents vs Wall Street" in London — organised by Primer, OpenStocks and AI Tinkerers, sponsored by OpenAI. The design combines strict temporal controls to prevent data leakage during backtesting, hybrid routing that mixes deterministic logic with LLM calls for the harder metrics, and independent voting across three decoupled sub-agents to catch extraction errors. On Home Depot ($HD) the agent scored 0.9684, placing 7th of 19 forecasts and beating the Wall Street consensus baseline.',
    tags: ['AI Agents', 'LLM', 'Multi-Agent Systems', 'Financial Forecasting', 'SEC Filings', 'Web Retrieval', 'Backtesting'],
    icon: '📈',
    color: '#2ECC71',
    highlights: [
      { label: 'Score', value: '0.9684' },
      { label: 'Ranking', value: '#7 of 19' },
      { label: 'Benchmark', value: 'Beat consensus' },
    ],
    linkedin: 'https://www.linkedin.com/posts/nevaerdogan_aiagents-llm-hackathon-activity-7495927459088056320-y3w9',
  },
  {
    id: 'taxim',
    title: 'TAXIM',
    subtitle: 'Traffic Analytics & eXtended Istanbul Model',
    description: 'An end-to-end AI project designed to predict the probability of finding a taxi in Istanbul. Built on a massive dataset of 19.2M rows and 35 features, combining IBB Traffic Sensor Data, meteorological data, POI & event data (football matches, concerts), and temporal cycles. Used K-Means Clustering for noise reduction, then applied XGBoost achieving 89.36% R² Score with MAE of just 4.95.',
    tags: ['XGBoost', 'K-Means', 'Python', 'Feature Engineering', 'Big Data'],
    icon: '🚕',
    color: '#FF69B4',
    highlights: [
      { label: 'Dataset', value: '19.2M rows' },
      { label: 'R² score', value: '89.36%' },
      { label: 'MAE', value: '4.95' },
    ],
    website: 'https://www.taximai.com/',
  },
  {
    id: 'gesture',
    title: 'Gesture Instrument',
    subtitle: 'Touchless webcam musical instrument with hand gestures',
    description: 'A real-time, touchless musical instrument that runs entirely on a standard webcam. Both hands are tracked simultaneously via a MediaPipe neural landmark model: the left hand selects the root note and the right hand selects the chord quality through two radial menus overlaid on the live feed. Every chord is synthesized live from pure sine waves shaped by an ADSR envelope and streamed sample-by-sample through sounddevice — zero pre-recorded audio.',
    tags: ['Python', 'MediaPipe', 'OpenCV', 'NumPy', 'sounddevice', 'Computer Vision', 'Audio Synthesis'],
    icon: '🎵',
    color: '#1ABC9C',
    highlights: [
      { label: 'Tracking', value: 'Dual-hand' },
      { label: 'Audio', value: 'Live synthesis' },
      { label: 'Chords', value: '8 qualities' },
    ],
    github: 'https://github.com/nevaerdogan/Hand-Gesture-Music',
  },
  {
    id: 'trnews',
    title: 'TRNewsSummarizer',
    subtitle: 'Benchmark of 10 Turkish extractive summarization methods',
    description: 'A systematic benchmark framework for Turkish extractive text summarization, evaluating 10 distinct methods on the Haberler-130 news dataset. It spans traditional NLP (TF-IDF, TextRank, LSA, NMF), sentence-embedding approaches (BERT, MMR, Centroid, K-Means, Title-Guided), and a quantum-inspired spectral clustering method. Each method is scored on ROUGE, coverage, diversity, redundancy, and runtime, exposed through both a Streamlit dashboard and a CLI batch pipeline.',
    tags: ['Python', 'NLP', 'sentence-transformers', 'scikit-learn', 'TextRank', 'ROUGE', 'Streamlit'],
    icon: '📰',
    color: '#6C5CE7',
    highlights: [
      { label: 'Methods', value: '10' },
      { label: 'Dataset', value: 'Haberler-130' },
      { label: 'Families', value: '3' },
    ],
    github: 'https://github.com/nevaerdogan/TRNewsSummarizer',
  },
  {
    id: 'cltv',
    title: 'Customer Lifetime Value Prediction',
    subtitle: 'CLTV prediction using BG-NBD and Gamma-Gamma models',
    description: "An advanced customer lifetime value prediction project using probabilistic models (BG-NBD and Gamma-Gamma) to forecast future customer value and develop data-driven retention strategies. Built on FLO's OmniChannel customer dataset (20,000+ customers), the system predicts expected revenue, identifies high-value customers for VIP programs, and optimizes marketing resource allocation through 4-tier customer segmentation.",
    tags: ['Python', 'BG-NBD Model', 'Gamma-Gamma Model', 'CLTV', 'Customer Segmentation', 'Pandas', 'Scikit-learn'],
    icon: '💰',
    color: '#F39C12',
    highlights: [
      { label: 'Dataset', value: '20K+ customers' },
      { label: 'Models', value: 'BG-NBD & Gamma-Gamma' },
      { label: 'Horizon', value: '6 months' },
    ],
    github: 'https://github.com/nevaerdogan/Customer_Lifetime_Value_Prediction',
  },
  {
    id: 'rfm',
    title: 'Customer Segmentation with RFM',
    subtitle: 'RFM analysis for targeted marketing strategies',
    description: 'A comprehensive customer segmentation project using RFM (Recency, Frequency, Monetary) analysis to help FLO develop targeted marketing strategies. The pipeline includes data preprocessing, RFM metric calculation, scoring (1-5 scale), and customer clustering. Applied to real business cases: premium brand launch targeting Champions & Loyal Customers, and discount campaigns for at-risk customers to maximize ROI and customer retention.',
    tags: ['Python', 'RFM Analysis', 'Customer Segmentation', 'Marketing Analytics', 'Pandas', 'Data Analysis'],
    icon: '🛍️',
    color: '#E74C3C',
    highlights: [
      { label: 'Dataset', value: '20K customers' },
      { label: 'Method', value: 'RFM scoring' },
      { label: 'Segments', value: '10+' },
    ],
    github: 'https://github.com/nevaerdogan/Customer_Segmentation_with_RFM',
  },
  {
    id: 'xside',
    title: 'XSIDE App Mockup',
    subtitle: 'Interactive iPhone mockup — LCW Challengers Ideathon',
    description: 'A fully interactive, web-based iPhone mockup for XSIDE, a fashion & lifestyle app concept for Gen Z shoppers, built for the LCWaikiki Challengers Ideathon. The prototype runs inside a pixel-accurate iPhone 15 Pro frame and covers the complete journey: onboarding, an Instagram-style home feed, an AI style assistant chat, gamified quizzes and mini-games (Stil Yakala), rewards, favorites, cart, and profile — all with CSS-only animations and requestAnimationFrame game loops.',
    tags: ['React 19', 'TypeScript', 'Vite', 'Tailwind CSS', 'UI/UX', 'Prototyping', 'Gamification'],
    icon: '📱',
    color: '#E84393',
    highlights: [
      { label: 'Screens', value: '10+' },
      { label: 'Stack', value: 'React + Vite' },
      { label: 'Deploy', value: 'Vercel' },
    ],
    github: 'https://github.com/nevaerdogan/LCW-Challengers-Competition_XSIDE-App-Mockup-',
  },
  {
    id: 'authorship',
    title: 'Authorship Detection with Text Mining',
    subtitle: 'Turkish author classification using BERT & ensemble models',
    description: 'A text mining project to classify authors based on Turkish language documents using NLP pipelines and machine learning. Compared traditional methods (TF-IDF, n-grams) with deep learning approaches (BERT embeddings). BERT + TF-IDF Fusion with MLP achieved the highest accuracy. Character-level n-grams proved especially effective for capturing stylistic patterns in Turkish text.',
    tags: ['Python', 'BERT', 'TF-IDF', 'NLP', 'XGBoost', 'Text Mining', 'Transformers'],
    icon: '✍️',
    color: '#3498DB',
    highlights: [
      { label: 'Best accuracy', value: '86%' },
      { label: 'Dataset', value: '1.2K texts' },
      { label: 'Best model', value: 'BERT + TF-IDF' },
    ],
    github: 'https://github.com/nevaerdogan/text-mining-for-authorship-detection',
  },
  {
    id: 'recsys',
    title: 'Hybrid Recommender System',
    subtitle: 'Personalized movie recommendation engine',
    description: 'Developed a sophisticated hybrid recommendation engine that intelligently combines Item-based and User-based Collaborative Filtering algorithms. The system analyzes user behavior patterns and item similarities to generate highly personalized movie suggestions. Implemented matrix factorization techniques and similarity metrics (cosine, Pearson correlation) to improve recommendation accuracy and handle the cold-start problem effectively.',
    tags: ['Collaborative Filtering', 'Python', 'Pandas', 'Scikit-learn', 'Recommendation Systems'],
    icon: '🎬',
    color: '#9B59B6',
    highlights: [
      { label: 'Approach', value: 'Hybrid' },
      { label: 'Methods', value: 'User & item CF' },
      { label: 'Domain', value: 'Movies' },
    ],
  },
  {
    id: 'mcq',
    title: 'MCQ Generator',
    subtitle: 'Automated multiple-choice question generation with transformer models',
    description: 'A robust "Context-to-MCQ" generation pipeline that produces linguistically fluent, pedagogically sound questions from raw text passages. Conducted comparative analysis of BART, T5, and GPT-2 on RACE dataset. BART-base achieved 100% Format Success Rate and 96.6/100 Quality Score, outperforming larger models. Optimized for consumer-grade hardware (RTX 3050 Ti 4GB) using FP16 Mixed Precision and Gradient Accumulation.',
    tags: ['BART', 'T5', 'NLP', 'PyTorch', 'Hugging Face', 'RACE Dataset'],
    icon: '📝',
    color: '#FFB6C1',
    highlights: [
      { label: 'Quality score', value: '96.6/100' },
      { label: 'Format success', value: '100%' },
      { label: 'Best model', value: 'BART-base' },
    ],
  },
];

const ExternalLink = ({ href, children }) => (
  <a className="btn" href={href} target="_blank" rel="noopener noreferrer">
    {children} <ArrowIcon />
  </a>
);

const Projects = () => {
  const navigate = useNavigate();
  const reduce = useReducedMotion();
  const [active, setActive] = useState(null);
  const closeRef = useRef(null);
  const close = () => setActive(null);

  // Escape closes; lock page scroll while the panel is open
  useEffect(() => {
    if (!active) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') close(); };
    document.addEventListener('keydown', onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    closeRef.current?.focus();
    return () => {
      document.removeEventListener('keydown', onKey);
      document.body.style.overflow = prev;
    };
  }, [active]);

  const openOnKey = (p) => (e) => {
    if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setActive(p); }
  };

  return (
    <section id="projects" className="section proj-section">
      <div className="container">
        <SectionHeader eyebrow="projects" title="Things I've built." lede="Open a card for the full story and links." />

        <ul className="proj-grid">
          {PROJECTS.map((p, i) => (
            <motion.li
              key={p.id}
              layoutId={`proj-${p.id}`}
              className={`proj has-corners ${p.featured ? 'proj--featured' : ''} ${p.wide ? 'proj--wide' : ''}`}
              style={{ '--pc': p.color }}
              role="button"
              tabIndex={0}
              aria-haspopup="dialog"
              onClick={() => setActive(p)}
              onKeyDown={openOnKey(p)}
              initial={reduce ? { opacity: 0 } : { opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: '0px 0px -60px 0px' }}
              whileHover={reduce ? undefined : { y: -5 }}
              transition={{ duration: 0.6, ease: EASE, delay: (i % 3) * 0.06 }}
            >
              <div className="proj-top">
                <span className="proj-icon" aria-hidden="true">{p.icon}</span>
                {p.badge && <span className="tag proj-badge">{p.badge}</span>}
              </div>
              <h3 className="proj-title t-display">{p.title}</h3>
              <p className="proj-sub">{p.subtitle}</p>
              <p className="proj-desc">{p.description}</p>
              <ul className="proj-tags">
                {p.tags.slice(0, 3).map((t) => <li className="tag" key={t}>{t}</li>)}
                {p.tags.length > 3 && <li className="tag tag--more">+{p.tags.length - 3}</li>}
              </ul>
              <span className="proj-open mono">Open <ArrowIcon /></span>
              <Corners />
            </motion.li>
          ))}
        </ul>
      </div>

      {createPortal(
        <AnimatePresence>
          {active && (
            <motion.div
              className="proj-overlay"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              transition={{ duration: 0.25 }}
              onClick={close}
            >
              <motion.div
                className="proj-modal"
                layoutId={`proj-${active.id}`}
                style={{ '--pc': active.color }}
                role="dialog"
                aria-modal="true"
                aria-labelledby="proj-modal-title"
                onClick={(e) => e.stopPropagation()}
                transition={{ type: 'spring', stiffness: 260, damping: 30 }}
              >
                <button ref={closeRef} type="button" className="proj-close" onClick={close} aria-label="Close">×</button>

                <div className="proj-modal-head">
                  <span className="proj-icon proj-icon--lg" aria-hidden="true">{active.icon}</span>
                  <div>
                    {active.badge && <span className="tag proj-badge">{active.badge}</span>}
                    <h3 id="proj-modal-title" className="t-display proj-modal-title">{active.title}</h3>
                    <p className="proj-sub">{active.subtitle}</p>
                  </div>
                </div>

                <motion.div
                  className="proj-modal-body"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.15, duration: 0.4, ease: EASE }}
                >
                  <p className="proj-modal-desc">{active.description}</p>

                  {active.highlights && (
                    <ul className="proj-stats">
                      {active.highlights.map((h) => (
                        <li className="proj-stat" key={h.label}>
                          <b>{h.value}</b>
                          <span>{h.label}</span>
                        </li>
                      ))}
                    </ul>
                  )}

                  <ul className="proj-tags">
                    {active.tags.map((t) => <li className="tag" key={t}>{t}</li>)}
                  </ul>

                  <div className="proj-links">
                    {active.caseStudy && (
                      <button type="button" className="btn btn-primary" onClick={() => navigate(active.caseStudy)}>
                        Read the case study <ArrowIcon />
                      </button>
                    )}
                    {active.github && <ExternalLink href={active.github}>GitHub</ExternalLink>}
                    {active.website && <ExternalLink href={active.website}>Live site</ExternalLink>}
                    {active.linkedin && <ExternalLink href={active.linkedin}>LinkedIn post</ExternalLink>}
                  </div>
                </motion.div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>,
        document.body
      )}
    </section>
  );
};

export default Projects;
